import classNames from 'classnames';
import React, { useEffect, useRef, useCallback, useMemo, useLayoutEffect, useReducer } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import Affix from '../../wui-affix/src';
import Icon from '../../wui-icon/src';
import { createChainedFunction, debounce, getNid, WebUI } from "../../wui-core/src/index";
import Dropdown from '../../wui-dropdown/src';
import Menu from '../../wui-menu/src';
import AntdAnchorLink from './AntdAnchorLink';
import { AnchorHorizontalState, AntdAnchorProps } from './iAnchor';

const { Item } = Menu;

const perchWidth = 78; // 出现左右箭头和下拉时,占位的宽度

const defaultProps = {
    prefixCls: 'wui-anchor',
    affix: true,
    offsetTop: 0,
    showInkInFixed: false,
    onChange: null,
    onClick: null
};

// ==================== Reducer 定义 ====================
type Action =
    | { type: 'SET_ACTIVE_KEY', payload: string }
    | { type: 'SET_SCROLL_X', payload: number }
    | { type: 'UPDATE_DIMENSIONS', payload: Partial<AnchorHorizontalState> }
    | { type: 'SET_NAVIGATION', payload: Partial<AnchorHorizontalState> }
    | { type: 'SET_MENU', payload: React.ReactNode }
    | { type: 'RESET_PAGINATION' };

const reducer = (state: AnchorHorizontalState, action: Action): AnchorHorizontalState => {
    switch (action.type) {
        case 'SET_ACTIVE_KEY':
            return { ...state, activeKey: action.payload };
        case 'SET_SCROLL_X':
            return { ...state, scrollX: action.payload };
        case 'UPDATE_DIMENSIONS':
            return { ...state, ...action.payload };
        case 'SET_NAVIGATION':
            return { ...state, ...action.payload };
        case 'SET_MENU':
            return { ...state, menuArr: action.payload };
        case 'RESET_PAGINATION':
            return { ...state, scrollX: 0 };
        default:
            return state;
    }
};

const AntdAnchor: React.FC<AntdAnchorProps> = (incomingProps) => {

    // ==================== Props ====================
    const props = { ...defaultProps, ...incomingProps };
    const {
        children, items, activeKey: propActiveKey, prefixCls,
        affix, offsetTop, getContainer, showInkInFixed, fieldid,
        style, className = '', onClick, onChange, activeIcon, curAnchorItem, onDropDownChange, targetOffset
    } = props;

    // ==================== State ====================
    const initialState: AnchorHorizontalState = {
        activeKey: propActiveKey,
        next: false,
        prev: false,
        prevStatus: false,
        nextStatus: false,
        menuArr: '',
        scrollX: 0,
        wrapperW: 0,
        anchorItems: [],
        allItemW: 0,
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    // ==================== Refs (实例属性) ====================
    const instanceRefs = useRef({
        startIndex: 0,
        endIndex: 0,
        activeKeyNum: 0,
        hrefArr: [] as string[],
        rightNumber: 1,
        leftNumber: 1,
        prevScreenItem: [] as number[],
        dropDownStatus: false,
        arrowStatus: false
    });

    // 单独维护 isScroll 状态
    const isScrollRef = useRef(true);
    const scrollHandRef = useRef<(() => void) | null>(null);

    const anchorRef = useRef<HTMLElement>(null!);
    const wrapperRef = useRef<HTMLElement>(null!);
    const contentDomRef = useRef<HTMLElement>(null!);
    const resizeObserverRef = useRef<any>(null);

    // ==================== 辅助函数 ====================
    // 缓存上一次读取的尺寸，避免重复回流
    const cachedDimensionsRef = useRef<{ wrapperW: number; anchorW: number }>({ wrapperW: 0, anchorW: 0 });
    // 初始化完成标志
    const isInitializedRef = useRef(false);

    const getOffsetWH = useCallback((node: HTMLElement) => {
        const prop = 'offsetWidth';
        return node ? node[prop] : 0;
    }, []);

    // ==================== 容器和元素查询公共方法 ====================
    /**
     * 获取滚动容器(统一的获取方式)
     * @returns 返回自定义容器或默认的 window
     */
    const getScrollContainer = useCallback(() => {
        return getContainer?.() || window;
    }, [getContainer]);

    /**
     * 根据 href 获取目标元素
     * @param href - 元素的 id (不带 # 前缀)
     * @param container - 可选的容器,如果不传则自动获取
     * @returns 目标 HTMLElement 或 null
     */
    const getTargetElement = useCallback((href: string, container?: HTMLElement | Window): HTMLElement | null => {
        const targetContainer = container ?? getScrollContainer();

        if (targetContainer === window) {
            // Window 环境: 使用 document.getElementById
            return document.getElementById(href);
        } else {
            // 自定义容器: 使用 querySelector
            const containerElement = targetContainer as HTMLElement;
            return containerElement.querySelector(`#${href}`) as HTMLElement | null;
        }
    }, [getScrollContainer]);

    /**
     * 获取容器的 Y 坐标(用于滚动位置计算)
     * @param container - 容器元素
     * @returns Y 坐标值
     */
    const getContainerY = useCallback((container: HTMLElement | Window): number => {
        if (container === window) {
            return 0;
        }
        return (container as HTMLElement).getBoundingClientRect().y;
    }, []);

    // 滚动到目标元素的通用方法
    const scrollToTarget = useCallback((href: string) => {
        const scrollOffset = targetOffset ?? (offsetTop || 0);
        const container = getScrollContainer();
        const targetElement = getTargetElement(href, container);

        if (!targetElement) return;

        if (container === window) {
            // Window 环境: 使用 window.scrollTo
            const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
                top: elementTop - scrollOffset,
                behavior: 'auto'
            });
        } else {
            // 自定义容器环境
            const containerElement = container as HTMLElement;
            // 检测容器是否可滚动
            const isScrollable = containerElement.scrollHeight > containerElement.clientHeight;

            if (isScrollable) {
                const elementTop = targetElement.offsetTop;
                containerElement.scrollTo({
                    top: elementTop - scrollOffset,
                    behavior: 'auto'
                });
            } else {
                // 如果container不是window且非滚动容器，使用scrollIntoView
                targetElement.scrollIntoView(true);
            }
        }
    }, [targetOffset, offsetTop, getScrollContainer, getTargetElement]);

    // ==================== 计算函数 ====================
    const calculateW = useCallback(() => {
        if (!wrapperRef.current || !anchorRef.current) return { anchorItems: [], allItemW: 0, wrapperW: 0, anchorW: 0 };

        const domNode = Array.from(wrapperRef.current.children[1].children);
        const allItems: { key: number | string, width: number, left: number, href: string }[] = [];
        let allItemW: number = 0;

        // 优化：一次性批量读取所有需要的offsetWidth，浏览器会批量处理，只触发一次回流
        const wrapperW = wrapperRef.current.offsetWidth;
        const anchorW = anchorRef.current.offsetWidth;

        // 缓存尺寸，供其他函数使用，避免重复回流
        cachedDimensionsRef.current = { wrapperW, anchorW };

        // 优化：一次性批量查询所有链接元素，避免在循环中使用 querySelector
        const linkElements = wrapperRef.current.querySelectorAll(`.${prefixCls}-link-title`);

        // 第一步：只读取 DOM 属性
        const elementsData = domNode.map((item, index) => {
            const element = item as HTMLElement;
            const computedStyle = getComputedStyle(element);
            const marginRight = parseFloat(computedStyle.marginRight);
            const marginLeft = parseFloat(computedStyle.marginLeft);
            const paddingLeft = parseFloat(computedStyle.paddingLeft);
            const paddingRight = parseFloat(computedStyle.paddingRight);
            const borderLeft = parseFloat(computedStyle.borderLeftWidth);
            const borderRight = parseFloat(computedStyle.borderRightWidth);

            // 从computedStyle计算实际宽度
            const contentWidth = parseFloat(computedStyle.width) || 0;
            const itemW = contentWidth + paddingLeft + paddingRight + borderLeft + borderRight + marginLeft + marginRight;
            const href = linkElements[index]?.getAttribute('href') || '';

            return {
                itemW,
                marginLeft,
                href,
                index
            };
        });

        // 第二步：计算累积位置和构建结果
        let cumulativeLeft = 0;
        elementsData.forEach((data) => {
            allItems.push({
                key: data.index,
                width: data.itemW,
                left: cumulativeLeft + data.marginLeft,
                href: data.href
            });

            cumulativeLeft += data.itemW;
            allItemW += data.itemW;
        });

        dispatch({
            type: 'UPDATE_DIMENSIONS',
            payload: { anchorItems: allItems, allItemW, wrapperW }
        });

        // 返回最新计算的值,供调用方直接使用
        return { anchorItems: allItems, allItemW, wrapperW, anchorW };
    }, [prefixCls]);

    // 获取首屏项
    const getFirstScreenItem = useCallback(() => {
        const { anchorItems, wrapperW } = state;
        const screenItemArr: number[] = [];
        let itemWtotal = 0;
        for (let i = 0; i < anchorItems.length - 1; i++) {
            itemWtotal += anchorItems[i].width;
            if (itemWtotal < wrapperW - perchWidth) {
                screenItemArr.push(i);
            }
        }
        return screenItemArr;
    }, [state]);

    // 滚动时计算startIndex
    const getStartIndex = useCallback((index: number) => {
        const { anchorItems, wrapperW } = state;
        let screeItemW = 0;
        const screenItemArr: number[] = [];
        for (let i = index; i > 0; i--) {
            screeItemW += anchorItems[i].width;
            if (screeItemW < wrapperW - perchWidth) {
                screenItemArr.push(i);
            }
        }
        return screenItemArr;
    }, [state]);

    // 是否显示左右箭头
    // 优化：使用传入的尺寸或缓存的尺寸，避免DOM读取导致的回流
    const setNextPrev = useCallback((latestDimensions?: { allItemW: number, wrapperW: number, anchorW?: number }) => {
        if (!anchorRef.current || !wrapperRef.current) return;

        // 优化：优先使用传入的值，其次使用缓存值，最后才使用 state
        const anchorW = latestDimensions?.anchorW ?? cachedDimensionsRef.current.anchorW;
        const wrapperW = latestDimensions?.wrapperW ?? cachedDimensionsRef.current.wrapperW ?? state.wrapperW;
        const allItemW = latestDimensions?.allItemW ?? state.allItemW;

        const containerWH = anchorW + 1;
        const minOffset = Number(containerWH) - Number(wrapperW);

        // 判断是否需要显示翻页按钮
        const needsNavigation = minOffset > 0 && allItemW > wrapperW - perchWidth;
        // 使用scrollX状态判断是否在最左边，避免getBoundingClientRect()调用导致的回流
        const isPrevDisabled = state.scrollX === 0;

        if (needsNavigation) {
            dispatch({
                type: 'SET_NAVIGATION',
                payload: {
                    prev: true,
                    next: true,
                    prevStatus: isPrevDisabled,
                    nextStatus: false
                }
            });
            // 使用 CSS 变量来控制 padding，减少直接 style 操作
            if (wrapperRef.current.style.paddingRight !== `${perchWidth}px`) {
                wrapperRef.current.style.paddingRight = perchWidth + 'px';
            }
        } else {
            dispatch({
                type: 'SET_NAVIGATION',
                payload: {
                    prev: false,
                    next: false,
                    prevStatus: true,
                    nextStatus: true
                }
            });
            if (wrapperRef.current.style.paddingRight !== '0px') {
                wrapperRef.current.style.paddingRight = '0px';
            }
        }
    }, [state.wrapperW, state.allItemW, state.scrollX]);

    // 下拉菜单项计算
    const getMenuItem = useCallback((itemIndex: number) => {
        const { prev, next, wrapperW, anchorItems } = state;
        if (!wrapperRef.current || !(prev || next) || anchorItems.length === 0) return;

        const domNode = Array.prototype.slice.call(wrapperRef.current.children[1].children);
        let widthNum = 0;
        const dropDownArr: { key: number, title: string }[] = [];
        const firstArr: number[] = [];
        const getFirstScreenItem = () => {
            const screenItemArr: number[] = [];
            let itemWtotal = 0;
            for (let i = 0; i < anchorItems.length - 1; i++) {
                itemWtotal += anchorItems[i].width;
                if (itemWtotal < wrapperW - perchWidth) {
                    screenItemArr.push(i);
                }
            }
            return screenItemArr;
        };

        const firstScreenItems = getFirstScreenItem();

        if (firstScreenItems.includes(Number(itemIndex))) {
            // 优化：使用缓存的anchorItems中的width数据，避免读取offsetWidth
            for (let i = 0; i < anchorItems.length; i++) {
                widthNum += anchorItems[i].width;
                if (widthNum > wrapperW - perchWidth) {
                    firstArr.push(i);
                }
            }
            for (let i = 0; i < domNode.length; i++) {
                if (firstArr.includes(i)) {
                    dropDownArr.push({ key: i, title: domNode[i].outerText });
                }
            }
        } else {
            const pageArr: number[] = [];

            // 计算可视区前项
            for (let i = itemIndex; i >= 0; i--) {
                widthNum += anchorItems[i]?.width || 0;
                if (widthNum > wrapperW - perchWidth) {
                    pageArr.push(i);
                }
            }

            // 计算可视区后项
            for (let i = 0; i < anchorItems.length; i++) {
                if (i > itemIndex) {
                    pageArr.push(i);
                }
            }

            // 计算dropdown要显示的项数据
            for (let i = 0; i < domNode.length; i++) {
                if (pageArr.includes(i)) {
                    dropDownArr.push({ key: i, title: domNode[i].outerText });
                }
            }
        }

        const menuArr = (
            dropDownArr.map((item) =>
                item && <Item key={item.key}>{item.title}</Item>
            )
        );

        dispatch({ type: 'SET_MENU', payload: menuArr });
    }, [state]);

    // 滚动页面时切换锚点项
    const scrollItem = useCallback((index: number) => {
        const { scrollX, anchorItems, wrapperW, next, allItemW } = state;
        const screenItemArr = getFirstScreenItem();

        if (!next) {
            return;
        }

        const scrollItemArr = getStartIndex(index);
        if (instanceRefs.current.activeKeyNum !== index && index > instanceRefs.current.activeKeyNum && !screenItemArr.includes(index) && scrollX !== -(allItemW - (wrapperW - perchWidth))) {
            let newScrollX = 0;
            let firsrScreeItemW = 0;
            if (screenItemArr.length === index) {
                for (let i = 0; i < screenItemArr.length + 1; i++) {
                    firsrScreeItemW += anchorItems[i].width;
                }
                newScrollX = scrollX - (firsrScreeItemW - wrapperW + perchWidth);
            } else {
                newScrollX = scrollX - anchorItems[index].width;
            }
            if (anchorItems.length - 1 === index) {
                dispatch({ type: 'SET_NAVIGATION', payload: { nextStatus: true } });
            }
            dispatch({ type: 'UPDATE_DIMENSIONS', payload: { scrollX: newScrollX, prevStatus: false } });
            instanceRefs.current.startIndex = scrollItemArr.sort()[0] - 1;
        }

        if (instanceRefs.current.activeKeyNum !== index && index > instanceRefs.current.activeKeyNum && !screenItemArr.includes(index)) {
            instanceRefs.current.activeKeyNum = index;
        }

        if (instanceRefs.current.activeKeyNum !== index && index < instanceRefs.current.activeKeyNum && !(index < screenItemArr[screenItemArr.length - 1]) && scrollX !== 0) {
            let newScrollX = 0;
            let firsrScreeItemW = 0;
            if (screenItemArr.length - 1 === index) {
                for (let i = 0; i < screenItemArr.length + 1; i++) {
                    firsrScreeItemW += anchorItems[i].width;
                }
                newScrollX = scrollX + (firsrScreeItemW - wrapperW + perchWidth);
            } else {
                newScrollX = scrollX + anchorItems[instanceRefs.current.activeKeyNum].width;
            }
            if (index === 1) {
                dispatch({ type: 'SET_NAVIGATION', payload: { prevStatus: true } });
                instanceRefs.current.startIndex = 0;
            }
            dispatch({ type: 'UPDATE_DIMENSIONS', payload: { scrollX: newScrollX, nextStatus: false } });
        }

        // 特殊情况
        if (instanceRefs.current.activeKeyNum !== index && index < instanceRefs.current.activeKeyNum && index < screenItemArr[screenItemArr.length - 1] && instanceRefs.current.prevScreenItem?.includes(instanceRefs.current.activeKeyNum) && instanceRefs.current.activeKeyNum !== instanceRefs.current.prevScreenItem[instanceRefs.current.prevScreenItem.length - 1]) {
            dispatch({ type: 'UPDATE_DIMENSIONS', payload: { scrollX: 0, nextStatus: false } });
            instanceRefs.current.prevScreenItem = [];
        }

        if (instanceRefs.current.activeKeyNum !== index && index < instanceRefs.current.activeKeyNum && !(index < screenItemArr[screenItemArr.length - 1])) {
            instanceRefs.current.activeKeyNum = index;
        }

        // 滚动到第一屏时左箭头失效
        if (instanceRefs.current.activeKeyNum !== index && index < instanceRefs.current.activeKeyNum && (index < screenItemArr[screenItemArr.length - 1])) {
            dispatch({ type: 'SET_NAVIGATION', payload: { prevStatus: true } });
        }

        getMenuItem(index);
    }, [state, getFirstScreenItem, getStartIndex, getMenuItem]);

    // ==================== 事件处理 ====================
    const scrollHand = useCallback(() => {
        if (!isScrollRef.current) return;

        let foundActiveItem = false;

        // 使用统一的方法获取容器和容器 Y 坐标
        const container = getScrollContainer();
        const containerY = getContainerY(container);

        instanceRefs.current.hrefArr.forEach((item, index) => {
            if (foundActiveItem) return; // 早期退出，避免不必要的计算

            // 使用统一的方法获取目标元素
            const element = getTargetElement(item, container);
            if (!element) return;

            const elementY = element.getBoundingClientRect().y;

            // 判断元素是否在可视区域
            let conditionMet = false;
            if (container === window) {
                // Window 环境: 元素顶部在视口顶部附近
                const rect = element.getBoundingClientRect();
                conditionMet = -rect.height < rect.y && rect.y <= 0;
            } else {
                // 自定义容器环境: 元素在容器可视区域内
                conditionMet = containerY - elementY <= elementY && elementY <= containerY;
            }

            if (conditionMet) {
                dispatch({ type: 'SET_ACTIVE_KEY', payload: item });
                foundActiveItem = true;
                if (!curAnchorItem) {
                    scrollItem(index);
                }
            }
        });
    }, [getScrollContainer, getContainerY, getTargetElement, curAnchorItem, scrollItem]);

    const handleSelect = useCallback((val: { href: string, title: string }) => {
        isScrollRef.current = false;
        const { anchorItems, wrapperW, scrollX, next, allItemW } = state;
        dispatch({ type: 'SET_ACTIVE_KEY', payload: val.href });

        if (onClick) onClick(val);
        if (onChange) onChange(val.href);

        if (next) {
            let itemWtotal = 0;
            const screenItemArr: number[] = [];
            for (let i = instanceRefs.current.startIndex; i < anchorItems.length - 1; i++) {
                itemWtotal += anchorItems[i].width;
                if (itemWtotal < wrapperW - perchWidth) {
                    screenItemArr.push(i);
                }
            }
            instanceRefs.current.startIndex = screenItemArr[0];
            let firsrScreeItemW = 0, newScrollX = 0;
            if (anchorItems[screenItemArr[screenItemArr.length - 1] + 1]?.href === val.href && val.href !== anchorItems[anchorItems.length - 1]?.href) {
                for (let i = screenItemArr[0]; i < screenItemArr[screenItemArr.length - 1] + 2; i++) {
                    firsrScreeItemW += anchorItems[i].width;
                }
                newScrollX = scrollX - (firsrScreeItemW + 13 - wrapperW + perchWidth);
                dispatch({ type: 'SET_SCROLL_X', payload: newScrollX });
                instanceRefs.current.activeKeyNum = screenItemArr[screenItemArr.length - 1] + 1;
                instanceRefs.current.startIndex = screenItemArr[screenItemArr.length - 1] + 1;
                instanceRefs.current.endIndex = screenItemArr[screenItemArr.length - 1] + 1;
            }
            if (anchorItems[screenItemArr[screenItemArr.length - 1] + 1]?.href === val.href && val.href === anchorItems[anchorItems.length - 1]?.href) {
                const offsetX = allItemW - (wrapperW - perchWidth);
                dispatch({ type: 'SET_SCROLL_X', payload: -offsetX });
                instanceRefs.current.endIndex = anchorItems.length - 1;
            }
            if (screenItemArr[0] !== 0) {
                const warpperX = wrapperRef.current.getBoundingClientRect().x;
                const anchorX = anchorRef.current.getBoundingClientRect().x;
                instanceRefs.current.prevScreenItem = screenItemArr;
                if (anchorItems[screenItemArr[0]]?.href === val.href) {
                    newScrollX = scrollX + (warpperX - anchorX - anchorItems[screenItemArr[0]].left) + 16;
                    dispatch({ type: 'SET_SCROLL_X', payload: newScrollX });
                    instanceRefs.current.endIndex = screenItemArr[screenItemArr.length - 1];
                }
            }
            if (val.href === anchorItems[anchorItems.length - 1].href) {
                instanceRefs.current.activeKeyNum = anchorItems.length - 1;
            }
            anchorItems.forEach((item, index) => {
                if (val.href === item.href) {
                    instanceRefs.current.activeKeyNum = index;
                }
            });
            if (val.href === anchorItems[0].href) {
                dispatch({ type: 'SET_SCROLL_X', payload: 0 });
                instanceRefs.current.activeKeyNum = 0;
                instanceRefs.current.startIndex = 0;
                instanceRefs.current.endIndex = 0;
            }
            getMenuItem(instanceRefs.current.endIndex);
        }
        // 应用 targetOffset 进行滚动
        setTimeout(() => {
            scrollToTarget(val.href);
        }, 100);
        // 复原isScroll
        setTimeout(() => {
            isScrollRef.current = true;
        }, 300); // 修改为300ms，确保scrollIntoView滚动完成后才恢复
    }, [state, onClick, onChange, getMenuItem, scrollToTarget]);

    // 右翻页
    const next = useCallback(() => {
        instanceRefs.current.dropDownStatus = false;
        instanceRefs.current.arrowStatus = true;
        dispatch({ type: 'SET_NAVIGATION', payload: { prevStatus: false } });

        const { scrollX, anchorItems, allItemW, wrapperW } = state;
        let itemWtotal = 0;
        const screenItemArr: number[] = [];
        for (let i = instanceRefs.current.startIndex; i < anchorItems.length - 1; i++) {
            itemWtotal += anchorItems[i].width;
            if (itemWtotal < wrapperW - perchWidth) {
                screenItemArr.push(i);
            }
        }

        instanceRefs.current.startIndex = screenItemArr[0];
        instanceRefs.current.endIndex = screenItemArr[screenItemArr.length - 1] + 1;

        let offsetX = 0;
        let afterW = anchorRef.current.getBoundingClientRect().width - (wrapperW - perchWidth) * instanceRefs.current.rightNumber;

        if (afterW <= wrapperW - perchWidth) {
            const newScrollX = -(allItemW - (wrapperW - perchWidth));
            dispatch({ type: 'UPDATE_DIMENSIONS', payload: { scrollX: newScrollX, nextStatus: true } });

            let nextScreenIndex = 0;
            const startIndexArr: number[] = [];
            for (let i = anchorItems.length - 1; i > 0; i--) {
                nextScreenIndex += anchorItems[i].width;
                if (nextScreenIndex >= wrapperW - perchWidth) {
                    startIndexArr.push(i);
                }
            }
            instanceRefs.current.startIndex = startIndexArr[0];
            instanceRefs.current.endIndex = anchorItems.length - 1;
        } else {
            for (let i = 0; i < anchorItems.length; i++) {
                for (let j = 0; j < screenItemArr.length; j++) {
                    if (i === screenItemArr[j]) {
                        offsetX += anchorItems[i].width;
                    }
                }
            }
            offsetX = wrapperW - perchWidth;
            const newScrollX = scrollX - offsetX;
            dispatch({ type: 'SET_SCROLL_X', payload: newScrollX });
            const nextStartIndex = instanceRefs.current.endIndex;
            instanceRefs.current.startIndex = nextStartIndex;
        }

        instanceRefs.current.rightNumber++;
        if (instanceRefs.current.leftNumber > 1) {
            instanceRefs.current.leftNumber--;
        }
        getMenuItem(instanceRefs.current.endIndex + 1);
    }, [state, getMenuItem]);

    // 左翻页
    const prev = useCallback(() => {
        instanceRefs.current.dropDownStatus = false;
        instanceRefs.current.arrowStatus = true;
        dispatch({ type: 'SET_NAVIGATION', payload: { nextStatus: false } });

        const { scrollX, anchorItems, wrapperW } = state;
        let itemWtotal = 0;
        const screenItemArr: number[] = [];
        for (let i = instanceRefs.current.endIndex - 1; i >= 0; i--) {
            itemWtotal += anchorItems[i]?.width;
            if (itemWtotal < wrapperW - perchWidth) {
                screenItemArr.push(i);
            }
        }

        let offsetX = 0;
        screenItemArr.sort();
        instanceRefs.current.startIndex = screenItemArr[0];
        instanceRefs.current.endIndex = screenItemArr[screenItemArr.length - 1] + 1;

        const beforeW = anchorRef.current.getBoundingClientRect().width - (wrapperW - perchWidth) * instanceRefs.current.leftNumber;
        if (beforeW <= wrapperW - perchWidth) {
            offsetX = 0;
            dispatch({ type: 'UPDATE_DIMENSIONS', payload: { scrollX: offsetX, prevStatus: true } });
            instanceRefs.current.startIndex = 0;
            instanceRefs.current.endIndex = 0;
        } else {
            for (let i = 0; i < anchorItems.length; i++) {
                for (let j = 0; j < screenItemArr.length; j++) {
                    if (i === screenItemArr[j]) {
                        offsetX += anchorItems[i].width;
                    }
                }
            }
            const newScrollX = scrollX + offsetX;
            dispatch({ type: 'SET_SCROLL_X', payload: newScrollX });
            const nextStartIndex = instanceRefs.current.startIndex;
            instanceRefs.current.endIndex = nextStartIndex;
        }

        instanceRefs.current.leftNumber++;
        instanceRefs.current.rightNumber--;
        getMenuItem(instanceRefs.current.endIndex + 1);
    }, [state, getMenuItem]);

    const onSelect = useCallback((key: Record<string, any>) => {
        instanceRefs.current.dropDownStatus = true;
        instanceRefs.current.arrowStatus = false;
        isScrollRef.current = false;
        const navNode = wrapperRef.current;
        const navNodeWH = getOffsetWH(navNode);

        const { scrollX, anchorItems, wrapperW } = state;
        let itemWtotal = 0;
        const screenItemArr: number[] = [];
        let scrollNum = 0;

        if (instanceRefs.current.startIndex < Number(key.key)) {
            let offsetW = 0;
            dispatch({ type: 'SET_NAVIGATION', payload: { prevStatus: false } });
            if (Number(key.key) === anchorItems.length - 1) {
                dispatch({ type: 'SET_NAVIGATION', payload: { nextStatus: true } });
            }
            const warpperX = wrapperRef.current.getBoundingClientRect().x;
            const anchorX = anchorRef.current.getBoundingClientRect().x;
            offsetW = anchorItems[Number(key.key)]?.left - (warpperX - anchorX) - (wrapperW - perchWidth) + anchorItems[Number(key.key)]?.width + 16;
            scrollNum = scrollX - offsetW;
            instanceRefs.current.endIndex = Number(key.key);

            for (let i = instanceRefs.current.endIndex; i > 0; i--) {
                itemWtotal += anchorItems[i]?.width;
                if (itemWtotal < wrapperW - perchWidth) {
                    screenItemArr.push(i);
                }
            }
            screenItemArr.sort();
            instanceRefs.current.startIndex = screenItemArr[0] - 1;
        } else {
            let beforeW = 0;
            for (let i = 0; i <= key.key; i++) {
                beforeW += anchorItems[i]?.width;
            }
            if (beforeW < navNodeWH - perchWidth) {
                scrollNum = 0;
                dispatch({ type: 'SET_NAVIGATION', payload: { prevStatus: true, nextStatus: false } });
                instanceRefs.current.startIndex = 0;
                instanceRefs.current.endIndex = 0;
            } else {
                let offsetW = 0;
                for (let i = Number(key.key) + 1; i <= instanceRefs.current.endIndex; i++) {
                    offsetW += anchorItems[i]?.width;
                }
                scrollNum = scrollX + offsetW;
                instanceRefs.current.startIndex = Number(key.key);
                instanceRefs.current.endIndex = Number(key.key);
            }
        }

        // 应用 targetOffset 进行滚动
        scrollToTarget(anchorItems[Number(key.key)]?.href);
        instanceRefs.current.activeKeyNum = Number(key.key);
        dispatch({ type: 'UPDATE_DIMENSIONS', payload: { scrollX: scrollNum, activeKey: anchorItems[Number(key.key)]?.href } });
        getMenuItem(key.key);

        if (onDropDownChange) {
            onDropDownChange(anchorItems[Number(key?.key)]?.href);
        }
    }, [state, getOffsetWH, getMenuItem, onDropDownChange, scrollToTarget]);

    const prevTransitionEnd = useCallback(() => {
        // 可以在这里添加过渡结束的逻辑
    }, []);

    // ==================== Effects ====================
    // 初始化 hrefArr
    useEffect(() => {
        let newHrefArr: string[] = [];
        let newActiveKey: string | undefined;

        // 设置 hrefArr
        if (items && items.length > 0) {
            newHrefArr = items.map(item => item.href);
            newActiveKey = propActiveKey || items[0]?.href;
        } else if (children) {
            React.Children.forEach(children, (child: any) => {
                newHrefArr.push(child.props.href);
            });
            newActiveKey = propActiveKey || newHrefArr[0];
        }

        // 只有当 hrefArr 真的变化时才更新
        const hrefArrChanged = JSON.stringify(instanceRefs.current.hrefArr) !== JSON.stringify(newHrefArr);

        if (hrefArrChanged) {
            instanceRefs.current.hrefArr = newHrefArr;
            if (newActiveKey) {
                dispatch({ type: 'SET_ACTIVE_KEY', payload: newActiveKey });
            }
        }
    }, [items, children, propActiveKey]);

    // 更新 scrollHandRef
    useEffect(() => {
        scrollHandRef.current = scrollHand;
    }, [scrollHand]);

    // 添加滚动监听（只注册一次，使用 ref 调用最新的 scrollHand）
    useEffect(() => {
        const handleScroll = () => {
            scrollHandRef.current?.();
        };

        // 使用统一的方法获取容器
        const container = getScrollContainer();
        const scrollTarget = container === window ? document : (container as HTMLElement);

        scrollTarget.addEventListener('scroll', handleScroll);

        // 清理
        return () => {
            scrollTarget.removeEventListener('scroll', handleScroll);
        };
    }, [getScrollContainer]); // 只依赖 getScrollContainer

    // 清理 ResizeObserver
    useEffect(() => {
        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, []);

    // 计算尺寸和翻页状态(只在初始挂载时执行)
    useLayoutEffect(() => {
        if (isInitializedRef.current) return;
        calculateW();
        setNextPrev();
        isInitializedRef.current = true;
    }, []); // eslint-disable-line

    // ResizeObserver
    useEffect(() => {
        const debouncedResize = debounce(() => {
            // 先计算尺寸,获取最新值
            const dimensions = calculateW();
            // 传入最新计算的尺寸
            setNextPrev(dimensions);
            setTimeout(() => {
                getMenuItem(0);
            }, 200);
        }, 200);

        resizeObserverRef.current = new ResizeObserver(debouncedResize);
        if (wrapperRef.current) {
            resizeObserverRef.current.observe(wrapperRef.current);
        }

        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, []); // eslint-disable-line

    // 监听 children/items 变化
    useEffect(() => {
        // 跳过初始化阶段，避免与 useLayoutEffect 重复执行
        if (!isInitializedRef.current) return;

        // 重置翻页相关状态
        instanceRefs.current.startIndex = 0;
        instanceRefs.current.endIndex = 0;
        instanceRefs.current.activeKeyNum = 0;
        instanceRefs.current.rightNumber = 1;
        instanceRefs.current.leftNumber = 1;

        dispatch({ type: 'RESET_PAGINATION' });

        requestAnimationFrame(() => {
            // 先计算尺寸,获取最新的 allItemW 和 wrapperW
            const dimensions = calculateW();
            // 将最新计算的尺寸传给 setNextPrev,避免使用闭包中的旧值
            setNextPrev(dimensions);
            getMenuItem(0);
        });
    }, [children, items]); // eslint-disable-line

    // 监听 propActiveKey 变化
    useEffect(() => {
        // 提前检查 propActiveKey 是否有效
        const isValidHref = propActiveKey ? instanceRefs.current.hrefArr.includes(propActiveKey) : false;

        // 如果 propActiveKey 无效，直接返回，不做任何处理
        if (propActiveKey && !isValidHref) {
            return;
        }

        if (propActiveKey && propActiveKey !== state.activeKey && isValidHref) {
            dispatch({ type: 'SET_ACTIVE_KEY', payload: propActiveKey });
        }
    }, [propActiveKey, state.activeKey]);

    // 监听 anchorItems 初始化完成后计算下拉菜单
    useEffect(() => {
        if (state.anchorItems.length > 0 && !state.menuArr) {
            getMenuItem(0);
        }
    }, [state.anchorItems, state.menuArr]); // eslint-disable-line

    // ==================== 渲染逻辑 ====================
    const handleItems = useMemo(() => {
        const linkArr: any[] = [];

        if (items && items.length > 0) {
            items.forEach((item: { key: string, href: string, title: string }) => {
                linkArr.push(
                    <AntdAnchorLink
                        expanded={item.href === state.activeKey}
                        fieldid={fieldid}
                        onSelect={handleSelect}
                        href={item.href}
                        title={item.title}
                        key={item.key}
                        isdirection={true}
                        activeIcon={activeIcon}
                    />
                );
            });
        } else {
            React.Children.forEach(children, (child: React.ReactElement) => {
                const linkHref = child.props.href;
                const childProps = {
                    onscrollTo: undefined,
                    eventKey: linkHref,
                    expanded: linkHref === state.activeKey,
                    onSelect: createChainedFunction(handleSelect, child.props.onSelect),
                    fieldid,
                    activeIcon,
                    isdirection: true,
                };
                linkArr.push(React.cloneElement(child, childProps));
            });
        }

        return linkArr;
    }, [items, children, state.activeKey, handleSelect, fieldid, activeIcon]);

    const adapterNid = getNid(props);

    const { prev: showPrev, next: showNext, prevStatus, nextStatus } = state;
    const showNextPrev = showPrev || showNext;

    const prevButton = (
        <span
            onClick={prevStatus ? undefined : prev}
            className={classNames({
                [`${prefixCls}-prev-arrow`]: true,
                [`${prefixCls}-disabled`]: prevStatus
            })}
            onTransitionEnd={prevTransitionEnd}
        >
            <Icon type="uf-anglepointingtoleft" />
        </span>
    );

    const nextButton = (
        <span
            onClick={!nextStatus ? next : undefined}
            className={classNames({
                [`${prefixCls}-next-arrow`]: true,
                [`${prefixCls}-disabled`]: nextStatus
            })}
        >
            <Icon type="uf-anglearrowpointingtoright" />
        </span>
    );

    const menu = (
        <Menu onClick={onSelect}>
            {state.menuArr}
        </Menu>
    );

    const moreTabsNode = (
        <Dropdown
            trigger={['hover']}
            overlay={menu}
            animation="slide-up"
            overlayClassName={`${prefixCls}-more-select-dropdown`}
        >
            <span className={`${prefixCls}-more-select`}>
                <Icon type="uf-listwithdots" />
            </span>
        </Dropdown>
    );

    const wrapperClass = classNames(
        `${prefixCls}-wrapper`,
        {
            [`${prefixCls}-horizontal`]: true
        },
        className,
    );

    const wrapperStyle = {
        maxHeight: offsetTop ? `calc(100vh - ${offsetTop}px)` : '100vh',
        ...style,
    };

    const anchorClass = classNames(prefixCls, {
        fixed: !affix && !showInkInFixed,
    });

    // 使用 useMemo 缓存 transform style，避免每次渲染都创建新对象
    const anchorTransformStyle = useMemo(() => ({
        transform: `translate3d(${state.scrollX}px,0,0)`
    }), [state.scrollX]);

    const anchorContent = (
        <div ref={wrapperRef as any} className={wrapperClass} style={wrapperStyle}>
            <div className={`${prefixCls}-btn`}>
                {showNextPrev && prevButton}
                {showNextPrev && nextButton}
                {showNextPrev && moreTabsNode}
            </div>
            <div className={anchorClass} style={anchorTransformStyle} ref={anchorRef as any}>
                {handleItems}
            </div>
        </div>
    );

    return (
        <div {...adapterNid} fieldid={fieldid} ref={contentDomRef as any}>
            {!affix ? (
                anchorContent
            ) : (
                <Affix offsetTop={offsetTop} target={getContainer} getPopupContainer={getContainer ? contentDomRef.current : null} zIndex={1000}>
                    {anchorContent}
                </Affix>
            )}
        </div>
    );
};

export default WebUI({ name: "anchor", defaultProps })(AntdAnchor);
